import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FortigatesService } from '@app/features/fortigates/shared/fortigates.service';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { Location } from '@app/shared/models/location.model';
import { Fortigate } from '@app/shared/models/fortigate/fortigate.model';
import { FortigateLocation } from '@app/shared/models/fortigate/fortigate-location.model';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-chain-wide-fortigates',
    templateUrl: './chain-wide-fortigates.component.html',
    styleUrls: ['./chain-wide-fortigates.component.css'],
    standalone: false
})
export class ChainWideFortigatesComponent implements OnInit {
    @ViewChild('fortigateTable') fortigateTable: MatTable<Fortigate>;
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    isLoading = false;
    onlyShowOffline: boolean;
    hideLabs: boolean;
    hideFutures: boolean;
    hideRemodels: boolean;
    filterText: string = null;
    allFortigates = [];
    fortigateLocations = [];
    locations = [];
    displayedColumns = ['storeNumber', 'locationName', 'locationTimeZone', 'locationStatus', 'fortigates'];
    dataSource = new MatTableDataSource<FortigateLocation>(this.fortigateLocations);

    constructor(private fortigatesService: FortigatesService, private locationsService: LocationsService) { }

    ngOnInit() {
        this.initComponent();
    }

    private async initComponent() {
        this.isLoading = true;
        await this.createDataSource();
        this.dataSource.paginator = this.paginator;
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'storeNumber': return item.location.number;
                case 'locationName': return item.location.name;
                case 'locationTimeZone': return item.location.timezone;
                case 'locationStatus': return item.location.status;
                default: return item[property];
            }
        };
        this.dataSource.sort = this.sort;
        this.fortigateTable.renderRows();
        
        this.isLoading = false;
    }

    async getAllFortigates() {
        try {
            const observable = await this.fortigatesService.getAllFortigates();
            const fortigates_from_response = await firstValueFrom(observable);
            if (typeof fortigates_from_response === 'string') {
                console.log("Fortigates timed out!")
                return;
            }
            fortigates_from_response["message"].forEach(fortigate_json => {
                let fortigate: Fortigate = fortigate_json;
                this.allFortigates.push(fortigate);
            });
        } catch (error) {
            console.log("Fortigates timed out!")
        }
    }

    async getAllLocations() {
        try {
            const observable = await this.locationsService.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                let location: Location = new Location;
                location.name = locations_json["core"]["locationName"];
                location.number = locations_json["locationNumber"];
                location.status = locations_json["locationStatus"]["locationStatus"];

                if (!locations_json["core"].hasOwnProperty('timeZone')
                    || !locations_json["core"]["timeZone"].hasOwnProperty('timeZoneName')) {
                    location.timezone = "";
                } else {
                    location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
                }

                // Some locations don't have operators, such as locations with a 'future' state
                if (!locations_json.hasOwnProperty('operator')) {
                    location.operatorName = "";
                } else {
                    location.operatorName = locations_json["operator"]["name"];
                }
                this.locations.push(location);
            });
        } catch (error) {
            console.log(error);
        }
    }

    sortFortigates() {
        let sortedFortigates = new Map<string, Fortigate[]>();
        for (let fortigate of this.allFortigates as Fortigate[]) {
            let storeNumber = "";

            if (fortigate.hostname != null) {
                storeNumber = fortigate.hostname.substring(0,5);
            }
            
            if (sortedFortigates.has(storeNumber)) {
                sortedFortigates.get(storeNumber).push(fortigate);
            } else {
                let fortigates = [];
                fortigates.push(fortigate);
                sortedFortigates.set(storeNumber, fortigates);
            }
        }
        return sortedFortigates;
    }
    sortLocations() {
        let sortedLocations = new Map<string, Location>();
        this.locations.forEach(location => {
            sortedLocations.set(location.number, location);
        });
        return sortedLocations;
    }

    async createDataSource(locations?) {

        if (locations == null) {
            await this.getAllLocations();
        } else {
            this.locations = locations;
        }

        await this.getAllFortigates();
        
        let sortedFortigates = this.sortFortigates();
        let sortedLocation = this.sortLocations();

        sortedFortigates.forEach((value: Fortigate[], key: string) => {
            if (sortedLocation.has(key)) {
                let fortigateLocation: FortigateLocation = new FortigateLocation;
                fortigateLocation.fortigates = value;
                fortigateLocation.location = sortedLocation.get(key);
                this.fortigateLocations.push(fortigateLocation);
            }
        });
    }

    onToggle() {
        this.applyFilter(this.filterText);
    }

    applyFilter(filterValue: string) {
        this.dataSource.filterPredicate = (data: FortigateLocation, filter: string) => {
            let hasOfflineFortigates = false;
            let containsFilterValue = false;

            // Handle offline fortigates
            if (this.onlyShowOffline) {
                for (let fortigate of data.fortigates) {
                    if (fortigate.conn_status == 2) { // offline
                        hasOfflineFortigates = true;
                    }
                }
            }

            // Handle filter text filtering
            if (filter == "null"
                || data.location.number.toLowerCase().includes(filterValue)
                || data.location.name.toLowerCase().includes(filterValue)
                || data.location.timezone.toLowerCase().includes(filterValue)
                || data.location.status.toLowerCase().includes(filterValue)) {
                containsFilterValue = true;
            }

            // Coalesce and make filtering decision
            if (this.hideLabs && data.location.number.charAt(0) == "9") {
                return false;
            } else if (this.hideFutures && data.location.status == "future") {
                return false;
            } else if (this.hideRemodels && data.location.status == "remodel") {
                return false;
            } else if (!containsFilterValue) {
                return false
            } else if (this.onlyShowOffline && !hasOfflineFortigates) {
                return false;
            } else {
                return true;
            }
        }

        if (filterValue == null || filterValue == "") { // handles case where no filter text is provided
            this.dataSource.filter = "null";
        } else {
            this.dataSource.filter = filterValue.trim().toLowerCase();
        }
    }

}